import { Timestamp } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import throttle from 'lodash/throttle';
import { Component } from 'react';

import { AddedCaller } from '../components/addedCaller';

import { PhoneBar } from './phoneBar';

type PropsT = {
  active: boolean;
  csid: number;
  // Csid of whatever Twilio Call instance Redux currently tracks, or null
  // when there is no active call. Changes every time
  // twilioEstablishedCall / twilioDisconnectedCall fires, which is the signal
  // we use to re-bind the volume listener.
  currentCallCsid: string | number | null;
  requesterId: string | null;
  recordingAsyncStatus: AsyncStatus;
  muteAsyncStatus: AsyncStatus;
  holdAsyncStatus: AsyncStatus;
  listenAsyncStatus: AsyncStatus;
  muted: boolean;
  onHold: boolean;
  recordingEnabled: boolean;
  isCurrentlyRecording: boolean;
  assignedAt: string | null;
  conversationPauseCallRecording: (payload: { csid: number }) => Promise<any>;
  conversationResumeCallRecording: (payload: { csid: number }) => Promise<any>;
  telephonySendDigits: (digit: string) => void;
  telephonySetHold: (payload: { setTo: boolean; csid: string }) => Promise<any>;
  telephonySetMute: (payload: { setTo: boolean; csid: string }) => Promise<any>;
  twilioConnectCall: (payload: {
    csid: string;
    muted: boolean;
    isOnlyListening?: boolean;
  }) => Promise<any>;
  twilioDisconnectCall: (payload: { csid: string }) => Promise<any>;
  addCallerRemoveCallerRequest: (payload: {
    userId: string;
    csid: string;
  }) => Promise<any>;
  addCallerSetOnHoldRequest: (payload: {
    userId: string;
    csid: string;
    hold: boolean;
  }) => Promise<any>;
  canListen: boolean;
  isListening: boolean;
  isAssigned: boolean;
  className?: string;
  authors: { [key: string]: User } | null;
  agentId?: string;
  extraParties: { [key: string]: User };
};

type StateT = {
  sounds: Array<{ input: number; output: number; isRecording: boolean }>;
  peakIn: number;
  peakOut: number;
};

export default class PhoneForm extends Component<PropsT, StateT> {
  // Reference to the Twilio Call we attached `handleVolumeUpdate` to. Tracked
  // so we can call removeListener on the *same* instance later, even after
  // window.currentPhoneCall has been swapped for a new Call.
  private subscribedCall: any = null;

  private volumeListenerRetryTimeout: ReturnType<typeof setTimeout> | null =
    null;

  constructor(props: PropsT) {
    super(props);
    this.state = {
      sounds: [],
      peakIn: 0,
      peakOut: 0,
    };
    this.handleVolumeUpdate = throttle(this.handleVolumeUpdate.bind(this), 200);
    this.renderCaller = this.renderCaller.bind(this);
  }

  componentDidMount() {
    this.bindVolumeListener();
  }

  componentDidUpdate(prevProps: PropsT) {
    // Re-bind whenever the Redux-tracked csid changes. That covers:
    // - first call after PhoneForm mounted with no active call
    // - takeover / transfer-back (Twilio swaps the Call instance)
    // - call ends (csid -> null, listener gets cleaned up)
    if (prevProps.currentCallCsid !== this.props.currentCallCsid) {
      this.bindVolumeListener();
    }
  }

  componentWillUnmount() {
    this.clearVolumeListenerRetry();
    this.unbindVolumeListener();
  }

  private bindVolumeListener(): void {
    this.clearVolumeListenerRetry();
    this.unbindVolumeListener();

    // A null csid means Redux no longer considers a call active. Clean up and
    // avoid re-attaching to a stale window.currentPhoneCall during teardown.
    if (this.props.currentCallCsid == null) return;

    // csid can be set in Redux before window.currentPhoneCall is assigned in
    // the Twilio runtime (mainly outbound dials). Schedule a retry instead of
    // bailing, so the meter still attaches once the global appears.
    const call = window.currentPhoneCall;
    if (!call) {
      this.scheduleVolumeListenerRetry();
      return;
    }

    call.on('volume', this.handleVolumeUpdate);
    this.subscribedCall = call;
  }

  private scheduleVolumeListenerRetry(): void {
    this.clearVolumeListenerRetry();

    // Nothing to bind to if Redux says there is no active call.
    if (this.props.currentCallCsid == null) return;

    this.volumeListenerRetryTimeout = setTimeout(() => {
      this.volumeListenerRetryTimeout = null;
      this.bindVolumeListener();
    }, 2000);
  }

  private clearVolumeListenerRetry(): void {
    if (this.volumeListenerRetryTimeout === null) return;

    clearTimeout(this.volumeListenerRetryTimeout);
    this.volumeListenerRetryTimeout = null;
  }

  private unbindVolumeListener(): void {
    if (!this.subscribedCall) return;
    try {
      this.subscribedCall.removeListener('volume', this.handleVolumeUpdate);
    } catch {
      // The Call may already be torn down by the SDK; nothing to do.
    }
    this.subscribedCall = null;
  }

  handleVolumeUpdate(inputVolume: number, outputVolume: number) {
    this.setState(({ peakOut, peakIn, sounds }: StateT) => {
      const output = peakOut ? outputVolume / peakOut : 0;
      const input = peakIn ? inputVolume / peakIn : 0;

      return {
        peakOut: outputVolume,
        peakIn: inputVolume,
        sounds: [
          ...sounds.slice(-999),
          {
            input,
            output,
            // Stamp the current recording state onto the sample so the
            // waveform color reflects pause/resume without a separate prop.
            isRecording: this.props.isCurrentlyRecording,
          },
        ],
      };
    });
  }

  toggleTelephonyHold = () => {
    const { telephonySetHold, onHold, csid, requesterId } = this.props;
    telephonySetHold({
      setTo: !onHold,
      csid,
      requesterId,
    } as any);
  };

  muteConversation = () => {
    this.props
      .telephonySetMute({ setTo: true, csid: this.props.csid } as any)
      .then((response: any) => {
        console.info('muteConversation response', response);
      })
      .catch((exception: any) => {
        console.warn('muteConversation exception', exception);
      });
  };

  unmuteConversation = () => {
    this.props
      .telephonySetMute({ setTo: false, csid: this.props.csid } as any)
      .then((response: any) => {
        console.info('unmuteConversation response', response);
      })
      .catch((exception: any) => {
        console.warn('unmuteConversation exception', exception);
      });
  };

  toggleTelephonyMute = () => {
    if (this.props.muted === true) {
      this.unmuteConversation();
    } else {
      this.muteConversation();
    }
  };

  toggleListening = () => {
    if (this.props.canListen && this.props.isListening === false) {
      this.listenConversation();
    } else if (this.props.canListen && this.props.isListening === true) {
      this.leaveConversation();
    } else {
      console.warn('toggleListening, Can not find conversation');
    }
  };

  listenConversation = () => {
    this.props
      .twilioConnectCall({
        csid: this.props.csid,
        muted: true,
        isOnlyListening: true,
      } as any)
      .catch(() => {});
  };

  leaveConversation = () => {
    this.props
      .twilioDisconnectCall({ csid: this.props.csid } as any)
      .catch(() => {});
  };

  toggleRecording = () => {
    if (this.props.isCurrentlyRecording) {
      this.props.conversationPauseCallRecording({ csid: this.props.csid });
    } else {
      this.props.conversationResumeCallRecording({ csid: this.props.csid });
    }
  };

  toggleAddCallerHold = (userId: string, onHold: boolean) => {
    const { addCallerSetOnHoldRequest, csid } = this.props;
    addCallerSetOnHoldRequest({
      hold: !onHold,
      csid,
      userId,
    } as any);
  };

  removeCallerRequest = (userId: string) => {
    const { addCallerRemoveCallerRequest, csid } = this.props;
    addCallerRemoveCallerRequest({
      csid,
      userId,
    } as any);
  };

  renderCaller({ user, onHold, callState }: any) {
    return (
      <AddedCaller
        key={user.id}
        className="added-caller"
        isOnHold={onHold}
        caller={user}
        onHoldToggle={() => this.toggleAddCallerHold(user.id, onHold)}
        onEndCall={() => this.removeCallerRequest(user.id)}
        callState={callState}
      />
    );
  }

  render() {
    const {
      assignedAt,
      muted,
      onHold,
      recordingAsyncStatus,
      muteAsyncStatus,
      holdAsyncStatus,
      listenAsyncStatus,
      telephonySendDigits,
      recordingEnabled,
      isCurrentlyRecording,
      isAssigned,
      canListen,
      isListening,
      className,
      extraParties,
    } = this.props;

    const { sounds } = this.state;

    const rootClass = classnames('phone-form', className);

    // Guard against assignedAt being null mid-transfer; a bare Timestamp
    // would render as NaN:NaN:NaN until the next assignment lands.
    const duration = assignedAt ? (
      <Timestamp counter time={assignedAt as any} />
    ) : (
      <span />
    );

    return (
      <div className={rootClass}>
        <PhoneBar
          className="phone-form__bar"
          isAssigned={isAssigned}
          canListen={canListen}
          isListening={isListening}
          duration={duration}
          canRecord={recordingEnabled}
          isRecording={isCurrentlyRecording}
          sounds={sounds}
          toggleRecording={this.toggleRecording}
          toggleListening={this.toggleListening}
          muted={muted}
          onHold={onHold}
          recordingAsyncStatus={recordingAsyncStatus}
          muteAsyncStatus={muteAsyncStatus}
          holdAsyncStatus={holdAsyncStatus}
          listenAsyncStatus={listenAsyncStatus}
          onHoldToggle={this.toggleTelephonyHold}
          onMuteToggle={this.toggleTelephonyMute}
          onDial={telephonySendDigits}
        />
        {Object.values(extraParties).map(this.renderCaller)}
      </div>
    );
  }
}
